// FindIdPage.tsx
import React, {useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {findId, sendVerificationCode, verifyCode} from '../../api/authApi';
import {defaultAlert} from "../../utils/alert";

export default function FindIdPage() {
    const navigate = useNavigate();
    const [memberName, setMemberName] = useState('');
    const [phone, setPhone] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [timeLeft, setTimeLeft] = useState(180); // 3분
    const [foundId, setFoundId] = useState('');

    // 타이머
    useEffect(() => {
        if (isCodeSent && timeLeft > 0) {
            const timer = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        } else if (isCodeSent && timeLeft === 0) {
            defaultAlert('인증번호 입력시간이 초과되었습니다. 다시 확인하여 주시기 바랍니다.');
            setIsCodeSent(false);
            setVerificationCode('');
        }
    }, [isCodeSent, timeLeft]);

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleSendCode = async () => {
        if (!memberName || !phone) {
            defaultAlert('이름과 휴대폰 번호를 입력해주세요.');
            return;
        }

        try {
            await sendVerificationCode({memberName, phone});
            setIsCodeSent(true);
            setTimeLeft(180);
            defaultAlert('인증번호가 발송되었습니다.');
        } catch (error) {
            console.error('Failed to send verification code:', error);
            defaultAlert('인증번호 발송에 실패했습니다.');
        }
    };

    const handleVerifyCode = async () => {
        if (!verificationCode) {
            defaultAlert('인증번호를 입력해주세요.');
            return;
        }

        try {
            const result = await verifyCode({phone, code: verificationCode});
            if (result.data.data) {
                setIsVerified(true);
                defaultAlert('인증이 완료되었습니다.');
            } else {
                defaultAlert('인증번호가 일치하지 않습니다.');
            }
        } catch (error) {
            console.error('Failed to verify code:', error);
            defaultAlert('입력한 정보가 맞지 않습니다. 다시 확인하여 주시기 바랍니다.');
        }
    };

    const handleFindId = async () => {
        if (!isVerified) {
            defaultAlert('휴대폰 인증을 완료해주세요.');
            return;
        }

        try {
            const response = await findId({memberName, phone, code: verificationCode});
            setFoundId(response.data.data);
            defaultAlert(`회원님의 아이디는 ${response.data.data} 입니다.`);
        } catch (error) {
            console.error('Failed to find id:', error);
            defaultAlert('입력한 정보가 맞지 않습니다. 다시 확인하여 주시기 바랍니다.');
        }
    };

    const handleConfirmId = () => {
        if (foundId) {
            navigate('/login');
        }
    };

    return (
        <div className="app">
            <header className="sub-header">
                <div className="header-inner">
                    <button type="button" className="backBtn" onClick={() => navigate(-1)}><span
                        className="blind">뒤로가기</span></button>
                </div>
            </header>
            <div className="bottom-btn">
                <button type="button" className="btn cancel" onClick={() => navigate(-1)}>
                    이전
                </button>
                <button
                    type="button"
                    className="btn confirm"
                    onClick={foundId ? handleConfirmId : handleFindId}
                    disabled={!isVerified}
                >
                    {foundId ? '확인' : '확인'}
                </button>
            </div>

            <main className="container">
                <section className="tit-box-wrap">
                    <h2 className="headline">아이디 찾기</h2>
                    <p className="subtit">
                        개인 정보를 통해 계정을 찾을 수 있습니다.<br/>
                        개인 정보를 입력해 주세요.
                    </p>
                </section>

                <section>
                    <div className="inner-box">
                        <form onSubmit={(e) => e.preventDefault()}>
                            <fieldset>
                                <legend>아이디 찾기 폼</legend>
                                <div className="form-box">
                                    <div className="form-item">
                                        <label htmlFor="name" className="input-tit">이름</label>
                                        <div className="input-box">
                                            <input
                                                type="text"
                                                id="memberName"
                                                placeholder="이름을 입력해 주세요."
                                                value={memberName}
                                                onChange={(e) => setMemberName(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-item">
                                        <label htmlFor="phone" className="input-tit">휴대폰 번호</label>
                                        <div className="input-set">
                                            <div className="input-box">
                                                <input
                                                    type="tel"
                                                    id="phone"
                                                    placeholder="휴대폰 번호를 입력해주세요"
                                                    value={phone}
                                                    onChange={(e) => setPhone(e.target.value)}
                                                    disabled={isCodeSent}
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className="check-btn"
                                                onClick={handleSendCode}
                                                disabled={isCodeSent || !memberName || !phone}
                                            >
                                                {isCodeSent ? '발송완료' : '인증하기'}
                                            </button>
                                        </div>
                                    </div>

                                    {isCodeSent && (
                                        <div className="form-item">
                                            <label htmlFor="verificationCode" className="input-tit">인증번호</label>
                                            <div className="input-set">
                                                <div className="input-box">
                                                    <input
                                                        type="text"
                                                        id="verificationCode"
                                                        placeholder="인증번호를 입력해주세요"
                                                        value={verificationCode}
                                                        onChange={(e) => setVerificationCode(e.target.value)}
                                                        disabled={isVerified}
                                                    />
                                                    {!isVerified &&
                                                        <span className="time">{formatTime(timeLeft)}</span>}
                                                </div>
                                                <button
                                                    type="button"
                                                    className="check-btn"
                                                    onClick={handleVerifyCode}
                                                    disabled={isVerified || timeLeft === 0}
                                                >
                                                    {isVerified ? '인증완료' : '확인'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </fieldset>
                        </form>
                    </div>
                </section>

                <section>
                    <div className="inner">
                        <p className="txt-guide">
                            비밀번호를 잊으셨나요? <Link to="/find-password">비밀번호 찾기</Link>
                        </p>
                    </div>
                </section>
            </main>
        </div>
    );
}